import ExternalLinks from '../../shared/externalLinks';
import React, { FunctionComponent } from 'react';
import { Box, Typography, Slider, TextField, Button, CircularProgress, Link } from '@mui/material';
import { currencyFormatter } from '../../shared/utils';
import { useNavigate } from 'react-router-dom';

interface Props {
    bulkPricingRules: any,
    serviceSettings: any
}

const Pricing: FunctionComponent<Props> = ({ bulkPricingRules, serviceSettings }) => {

    const navigate = useNavigate();
    const { baseCreditPrice } = serviceSettings;
    const [months, setMonths] = React.useState<number>(0);
    const [credits, setCredits] = React.useState<number>(0);
    const [properties, setProperties] = React.useState<number>(1);
    const [price, setPrice] = React.useState<number>(0);
    const [discountPercentage, setDiscountPercentage] = React.useState<number>(0);
    const [pricePerNight, setPricePerNight] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const getDiscountPercentage = () => {
        var discount = 0;

        if (bulkPricingRules != null) {
            for (var rule of bulkPricingRules) {
                if (credits >= rule.quantity && discount < rule.percentageDiscount) {
                    discount = rule.percentageDiscount;
                }
            }
        }
        return discount;
    }

    const recalculatePricing = () => {
        const basePrice = credits * baseCreditPrice;
        const discount = getDiscountPercentage();
        const discountAmount = basePrice * (discount / 100);
        const newPrice = basePrice - discountAmount;
        const newPricePerNight = newPrice / (credits);
        setPrice(newPrice);
        setDiscountPercentage(discount);
        setPricePerNight(newPricePerNight)
    }

    React.useEffect(recalculatePricing, [credits, properties])

    const handleSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
        const newMonths = value as number;
        setMonths(newMonths);
        const newCredits = newMonths * properties * 30;
        setCredits(newCredits);
    };

    const handlePropertiesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newProperties = Number(event.target.value);
        setProperties(newProperties);
        const newCredits = months * newProperties * 30;
        setCredits(newCredits);
    };

    const onSignUpClick = () => {
        setIsLoading(true);
        if (credits) {
            navigate(`/registration`, { state: { additionalCredits: credits, initialMonths: months, initialProperties: properties } });
        }
        else {
            navigate(`/registration`);
        }
    }

    if (isLoading) {
        return (<Box height="80vh" display="flex" flexDirection="column" justifyContent="center">
            <Box display="flex" justifyContent="center"><CircularProgress /></Box>
        </Box>)
    }

    return (<Box m={2} display="flex" flexDirection="column" width="90vm" style={{ textAlign: "center" }}>
        <Typography variant="h4" style={{ fontWeight: 800, fontSize: 28 }} gutterBottom>Only pay for what you use.</Typography>
        <Typography style={{ marginTop: 20 }} variant="h6">Get a subscription and use it at your property.</Typography>
        <Typography gutterBottom variant="h6">Off season? Disable the property and pay nothing.</Typography>
        <Typography style={{ marginTop: 20 }} gutterBottom>Your first month is free. Slide the bar to add more.</Typography>
        <Box display="flex" justifyContent={"center"}>
            <Box display="flex" p={2} flexDirection={"column"} maxWidth={600} style={{ backgroundColor: 'silver' }} marginBottom={2}>
                <Typography style={{ fontSize: 14 }} gutterBottom>
                    In the AIPEX portal, you can easily enable or disable the Virtual Concierge service for any of your properties.
                    Your subscription only counts down for properties that are actively using the service, giving you full flexibility to pause during the off-season.
                    Pricing starts at ${baseCreditPrice} per active property, with discounts available for longer terms or multiple properties.
                </Typography>
                <Box marginTop={2}>
                    <Link style={{ cursor: 'pointer', color: '#0645ad' }} target="_blank" rel="noopener noreferrer" href={ExternalLinks.MARKETING.SUPPORT.GETTING_STARTED.SUBSCRIPTION_CREDITS}>
                    Learn More
                    </Link>
                </Box>
            </Box>
        </Box>
        <Box marginTop={8} display="flex" justifyContent="center">
            <Typography style={{ paddingTop: 5 }}>Number of properties/devices: </Typography>
            <Box marginLeft={2} width={48}>
                <TextField type="number" variant="standard" value={properties} onChange={handlePropertiesChange} inputProps={{ style: { textAlign: 'center' }, min: 0 }} />
            </Box>
        </Box>
        <Box display="flex" justifyContent="center">
            <Box width="600px">
                <Slider step={1} max={36} min={0} defaultValue={0} valueLabelDisplay="auto" onChange={handleSliderChange} />
            </Box>
        </Box>
        <Box display="flex" justifyContent="center">
            <Typography>Months to add: {months}</Typography>
            <Typography style={{ marginLeft: 16 }}>Price: {currencyFormatter.format(price)} {discountPercentage ? `(${discountPercentage}% Off)` : ''}</Typography>
        </Box>

        <Box display="flex" justifyContent="center">
            <Box marginTop="16px" padding={"8px"} width="500px" style={{ backgroundColor: "silver" }} display="flex" flexDirection="column" justifyContent="center">
                {properties < 1
                    ? <Typography>Please enter a valid number of properties</Typography>
                    : <Typography>Most properties aren't fully booked, but to give you an idea - if the service was enabled during guest stays, that's just:</Typography>
                }
                {credits > 0 && (<>
                    <Box display="flex" justifyContent="center" m={3}>
                        <Typography variant={'h4'}>{currencyFormatter.format(pricePerNight)}</Typography>
                        <Box width={80}>
                            <Box width={"12px"}><Typography style={{ marginLeft: "8px", fontWeight: "bold", lineHeight: "16px" }}>per booked night</Typography></Box>
                        </Box>
                    </Box>
                </>)}
            </Box>
        </Box>

        <Box marginTop="24px" display="flex" justifyContent="center" >
            <Button color="secondary" variant="contained" onClick={onSignUpClick}>Sign Up</Button>
        </Box>
    </Box>
    );
}

export default Pricing;